import { IconExternal } from '@components/icons';
import { dappStore } from '@components/store';
import Image from 'next/image';
import { naturalNumber } from '@components/eth/Utils';
import { useActivePositionId } from '@components/store/getters';
import { Position } from 'types';
import { useState } from 'react';
import { createNftImageLocation } from 'utils/createNftImageLocation';
import { createOpenseaURL } from 'utils/createOpenseaURL';
import {
  NftWrapper,
  DataWrap,
  NftImageWrapper,
  InfoContainer,
  StyledIconExternal,
  StyledLink,
  ExternalStyledLink,
  StyledRow,
  StyledSpan,
  Text,
  ConfirmationWrapper,
  LinksWrapper,
  StyledButton,
} from './ViewNftModal.styles';
import { useEffect } from 'react';
import Link from '@components/common/Link';
import Button from '../Button';

interface ViewNftModalProps {
  confirmationMode?: boolean;
  activePosition: Position;
}

export default function ViewNftModal({ confirmationMode, activePosition }: ViewNftModalProps) {
  const positionId = useActivePositionId();
  const [imageSrc, setImageSrc] = useState('');
  useEffect(() => {
    setImageSrc(createNftImageLocation(positionId));
  }, [positionId]);
  return (
    <>
      {confirmationMode && renderConfirmationHeader()}
      <NftWrapper>
        <DataWrap>
          <InfoContainer>
            <StyledRow>
              <StyledSpan type="span">Collateral</StyledSpan>
              <StyledSpan type="span" isValue>
                {naturalNumber(activePosition.collateral)} USD
              </StyledSpan>
            </StyledRow>
            <StyledRow>
              <StyledSpan type="span">Borrowed</StyledSpan>
              <StyledSpan type="span" isValue>
                {naturalNumber(activePosition.ousdBorrowed)} OUSD
              </StyledSpan>
            </StyledRow>
            {!confirmationMode && (
              <StyledRow>
                <StyledSpan type="span">Est. Net APY</StyledSpan>
                <StyledSpan type="span" isValue>
                  {activePosition.apy > 0 ? naturalNumber(activePosition.apy.toString()) + '%' : '--'}
                </StyledSpan>
              </StyledRow>
            )}
            <StyledRow>
              <StyledSpan type="span">Position Lifetime</StyledSpan>
              <StyledSpan type="span" isValue>
                {activePosition.lifetimeInDays} Days
              </StyledSpan>
            </StyledRow>
          </InfoContainer>
        </DataWrap>
        <NftImageWrapper>
          <StyledRow nftContainer>
            {imageSrc && (
              <Image
                src={imageSrc}
                width="160"
                height="160"
                alt="NFT"
                onError={() => setImageSrc('/example-nft.png')}
              />
            )}
          </StyledRow>
          <InfoContainer>
            <StyledRow>
              <StyledSpan type="span">Your Tradable Position</StyledSpan>
            </StyledRow>
            <StyledRow withMarginBottom>
              <StyledSpan type="span">
                NFT <strong>#{positionId}</strong>
              </StyledSpan>
            </StyledRow>
            <StyledRow>
              <ExternalStyledLink variant="thick" href={createOpenseaURL(positionId)} target="_blank" rel="noreferrer">
                View on OpenSea
                <StyledIconExternal />
              </ExternalStyledLink>
            </StyledRow>
          </InfoContainer>
        </NftImageWrapper>
      </NftWrapper>
    </>
  );
}

function renderConfirmationHeader() {
  const latestTx = dappStore.getState().latestTx;

  const handleOpenAnotherPositionClick = () => {
    dappStore.setState({ activePositionId: null, activePosition: null, isViewNft: false });
  };

  return (
    <>
      <ConfirmationWrapper>
        <Text>
          <p>
            Congratulations! You have opened a new position. You can now trade it on any NFT marketplace.&nbsp;
            <Link href="https://docs.archimedesfi.com/" target="_blank" rel="noreferrer">
              View documentation here
            </Link>
          </p>
          <p>
            <em>Note:</em> There was a surplus of ARCH from this transaction. We have transferred the amount to your
            wallet.
          </p>
        </Text>
        <LinksWrapper>
          <StyledButton onClick={handleOpenAnotherPositionClick}>Open Another Position</StyledButton>
          {latestTx && (
            <>
              <Link href={`https://etherscan.io/tx/${latestTx}`} target="_blank" variant="outline" rel="noreferrer">
                View Transaction on Etherscan
                <IconExternal />
              </Link>
            </>
          )}
        </LinksWrapper>
        <h4>Your Position Details</h4>
      </ConfirmationWrapper>
    </>
  );
}
